import { fa, toman } from './fa';
import { alreadyFired, sourceLabel, type Arrival } from './ArrivalsScreen';

/*
 * One incoming order, opened.
 *
 * The list card shows what the order is; this shows everything about it and
 * everything that can be done to it. It exists because the card had no tap
 * target at all: once fired orders started being listed, a cashier could see
 * an order and have no way to touch it — the fire button is withheld on a
 * fired order and claiming is for kiosk orders, so the card had no control on
 * it whatsoever. Tapping did nothing, and our user said so.
 *
 * Reprint is the action that matters here. The question a cashier is actually
 * answering at this screen is "the pass says they never got this" — and the
 * answer is another ticket, not a second order.
 */
export function ArrivalSheet({
  arrival,
  onClose,
  onFire,
  onTake,
  onReprint,
  onPay,
  busy,
  error,
}: {
  arrival: Arrival;
  onClose: () => void;
  onFire: (id: string) => void;
  onTake: (id: string) => void;
  onReprint: (ticketId: string) => void;
  /*
   * Take payment for this order's table.
   *
   * Handed the CHECK, not the arrival: an arrival is one round and a table's
   * bill is all of them, so paying the arrival's own total would charge a
   * table that ordered twice for one round.
   */
  onPay: (checkId: string) => void;
  busy: boolean;
  error: string | null;
}) {
  const a = arrival;
  const src = sourceLabel(a.kind);
  const fired = alreadyFired(a);

  return (
    <>
      <div
        style={{ position: 'fixed', inset: 0, zIndex: 50, background: 'rgba(32,30,29,.45)' }}
        onClick={onClose}
      />
      <div
        dir="rtl"
        role="dialog"
        aria-label="جزئیات سفارش"
        style={{
          position: 'fixed', zIndex: 51, top: '50%', left: '50%',
          transform: 'translate(-50%,-50%)', width: 'min(94vw, 520px)',
          maxHeight: '88vh', overflowY: 'auto',
          background: 'var(--luma-card)', borderRadius: 16,
          border: '1px solid var(--luma-line-strong)', padding: 20,
          display: 'grid', gap: 14, boxShadow: '0 18px 50px rgba(0,0,0,.22)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span
            style={{
              fontSize: 12.5, fontWeight: 700, borderRadius: 999, padding: '2px 9px',
              color: 'var(--luma-muted)', background: 'var(--luma-accent-tint)',
              border: '1px solid var(--luma-line-strong)', whiteSpace: 'nowrap',
            }}
          >
            <span aria-hidden="true" style={{ marginInlineEnd: 5 }}>{src.icon}</span>
            {src.text}
          </span>
          <strong style={{ fontSize: 19 }}>
            {a.kind === 'table' && a.tableLabel ? a.tableLabel : fa(a.number)}
          </strong>
          <span className="pos-num" style={{ fontSize: 16, fontWeight: 700, marginInlineStart: 'auto' }}>
            {toman(a.totalRial)}
          </span>
        </div>

        {/*
          Times, because "how long has this been waiting" is the question a
          cashier opens an order to answer.
        */}
        <div style={{ fontSize: 13, color: 'var(--luma-muted)', display: 'grid', gap: 5 }}>
          <div>{a.waitingMinutes > 0 ? `${fa(String(a.waitingMinutes))} دقیقه پیش رسیده` : 'همین الان رسیده'}</div>
          {fired && (
            <div>
              {a.firedBy === 'system:auto'
                ? 'به‌صورت خودکار به آشپزخانه رفته'
                : `به آشپزخانه رفته${a.firedBy ? ` — ${a.firedBy}` : ''}`}
            </div>
          )}
          {!fired && <div>هنوز به آشپزخانه نرفته</div>}
          {a.paid ? <div>پرداخت‌شده</div> : <div>تسویه نشده</div>}
          {a.takenBy && <div>{`${a.takenBy} دریافت کرده`}</div>}
        </div>

        <div style={{ borderTop: '1px solid var(--luma-line)', paddingTop: 12, display: 'grid', gap: 8 }}>
          {a.lines.length === 0 ? (
            // Says so rather than rendering an empty box. An order with no
            // readable lines is a thing to ask about, not a blank panel.
            <div style={{ fontSize: 13, color: 'var(--luma-muted)' }}>
              اقلام این سفارش در دسترس نیست
            </div>
          ) : (
            a.lines.map((l, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'baseline', gap: 10 }}>
                <span className="pos-num" style={{ fontWeight: 700, minWidth: 34 }}>
                  {fa(String(l.quantity))}×
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 15 }}>{l.name}</div>
                  {l.modifiers.length > 0 && (
                    <div style={{ fontSize: 12.5, color: 'var(--luma-muted)' }}>
                      {l.modifiers.map((m) => m.name).join('، ')}
                    </div>
                  )}
                </div>
                <span className="pos-num" style={{ fontSize: 13.5 }}>{toman(l.lineTotalRial)}</span>
              </div>
            ))
          )}
        </div>

        {error && (
          <div style={{
            fontSize: 13, color: 'var(--luma-tint-danger-text, #8A2D2D)',
            background: 'var(--luma-tint-danger-bg, rgba(138,45,45,.08))',
            border: '1px solid var(--luma-tint-danger-border, rgba(138,45,45,.25))',
            borderRadius: 10, padding: '8px 10px',
          }}>
            {error}
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          {/*
            Payment, for a table that still owes.
            Offered only with a check to settle and only while unpaid: a kiosk
            order is paid at the screen and carries no check, and a button
            there would invite collecting money twice for one coffee.

            The total on this sheet is ONE round. The handler fetches the
            table's actual bill rather than paying what is shown, because a
            table that ordered twice owes more than the round a cashier
            happened to tap.
          */}
          {a.checkId && !a.paid && (
            <button
              onClick={() => onPay(a.checkId as string)}
              disabled={busy}
              style={{
                flex: 1, minWidth: 150, height: 48, borderRadius: 12, border: 'none',
                background: busy ? 'var(--pos-disabled)' : 'var(--luma-accent)',
                color: '#fff', fontWeight: 800, fontSize: 15,
              }}
            >
              {busy ? 'لطفاً صبر کنید…' : 'رفتن به پرداخت'}
            </button>
          )}
          {/*
            Firing only while it is unfired. A second fire is idempotent, so
            offering it would be harmless to the data and misleading to the
            person: they would press it, see nothing change, and go and argue
            with the kitchen about an order that was never in doubt.
          */}
          {!fired && (
            <button
              onClick={() => onFire(a.id)}
              disabled={busy}
              style={{
                flex: 1, minWidth: 150, height: 48, borderRadius: 12,
                // Secondary when payment is also on offer: two accented
                // buttons side by side make neither one the obvious next step.
                border: a.checkId && !a.paid ? '1px solid var(--luma-line-strong)' : 'none',
                background: busy
                  ? 'var(--pos-disabled)'
                  : a.checkId && !a.paid ? 'var(--luma-card)' : 'var(--luma-accent)',
                color: a.checkId && !a.paid ? 'var(--luma-ink)' : '#fff',
                fontWeight: 800, fontSize: 15,
              }}
            >
              {busy ? 'لطفاً صبر کنید…' : 'ارسال به آشپزخانه'}
            </button>
          )}

          {/*
            Reprint, which is why this sheet exists. Only once a ticket exists
            — there is nothing to reprint before that.
          */}
          {a.ticketId && (
            <button
              onClick={() => onReprint(a.ticketId as string)}
              disabled={busy}
              style={{
                flex: 1, minWidth: 150, height: 48, borderRadius: 12,
                background: 'var(--luma-card)', color: 'var(--luma-ink)',
                border: '1px solid var(--luma-line-strong)', fontWeight: 700, fontSize: 14.5,
              }}
            >
              {busy ? 'لطفاً صبر کنید…' : 'چاپ دوباره رسید آشپزخانه'}
            </button>
          )}

          {/* Claiming is for an unattended kiosk order, as on the list. */}
          {a.kind !== 'table' && !a.taken && (
            <button
              onClick={() => onTake(a.id)}
              disabled={busy}
              style={{
                flex: 1, minWidth: 130, height: 48, borderRadius: 12,
                background: 'var(--luma-card)', color: 'var(--luma-muted)',
                border: '1px solid var(--luma-line-strong)', fontWeight: 700, fontSize: 14,
              }}
            >
              این سفارش را دارم
            </button>
          )}
        </div>

        <button
          onClick={onClose}
          style={{
            height: 44, borderRadius: 12, background: 'transparent',
            border: '1px solid var(--luma-line)', color: 'var(--luma-muted)',
            fontWeight: 700, fontSize: 14,
          }}
        >
          بستن
        </button>
      </div>
    </>
  );
}
